/** Карточка рекламной кампании (боковая панель, `card=campaign:<номер>`): шапка (тип, юрлицо) →
 *  KPI периода против сравнения + сверка «начислено / статистика» → расход и заказы по дням →
 *  артикулы кампании.
 *
 *  Номер кампании = `obj_id` витрины у `obj_kind='campaign'` = `campaign_id` таблиц рекламы Ozon
 *  (`build-mart` ищет статистику по `str(campaign_id)`), поэтому один ключ годится для обоих
 *  источников:
 *  - начислено (факт финотчёта) — `/summary?scope=obj:campaign:<id>`;
 *  - статистика рекламного кабинета — `GET /campaign/{id}` (404 — кампании нет нигде: только шапка).
 *  Фильтры страницы (период, сравнение, юрлицо, общие расходы) — как есть. Каждый блок грузится
 *  сам: свой скелетон, своя ошибка текстом. */
import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { CampaignSku, OzCampaign, OzFilters, OzSummary } from "@/api/ozonAnalytics";
import { axisTick } from "@/lib/chart-theme";
import { useIsDark } from "@/lib/use-is-dark";
import { cn } from "@/lib/utils";
import { accrualCheck, ACCRUED_HINT, accruedKpi, campaignKpis, STATS_HINT } from "./campaignKpi";
import { Block, Skeleton } from "./cardBlocks";
import { compact, int, pct, rub } from "./format";
import { KpiGrid } from "./pnl";
import { useCampaign, useSummary } from "./queries";
import { CardButton, ErrorBox } from "./shared";
import type { CardRef } from "./state";
import { tdNum, thClass } from "./ui";

export function CampaignCard({
  campaignId,
  filters,
  warning,
  titleId,
  onOpenCard,
}: {
  campaignId: string;
  filters: OzFilters;
  warning: string | null;
  /** id заголовка — на него ссылается aria-labelledby панели */
  titleId: string;
  /** переход к карточке артикула кампании внутри панели */
  onOpenCard: (card: CardRef) => void;
}): React.ReactElement {
  const campaign = useCampaign(campaignId, filters);
  // кампании нет ни в статистике, ни в начислениях (у юрлица фильтра) — только шапка с ошибкой
  const notFound =
    (campaign.error as { response?: { status?: number } } | null)?.response?.status === 404;
  return (
    <div className="space-y-5">
      <Header q={campaign} campaignId={campaignId} titleId={titleId} />
      {!notFound && (
        <CampaignBlocks
          campaignId={campaignId}
          filters={filters}
          warning={warning}
          campaign={campaign}
          onOpenCard={onOpenCard}
        />
      )}
    </div>
  );
}

type CampaignQuery = ReturnType<typeof useCampaign>;

function Header({
  q,
  campaignId,
  titleId,
}: {
  q: CampaignQuery;
  campaignId: string;
  titleId: string;
}): React.ReactElement {
  const c = q.data;
  return (
    <div className="space-y-1" data-testid="campaign-header">
      <h2 id={titleId} className="text-xl font-semibold leading-tight tabular-nums">
        Кампания {campaignId}
      </h2>
      {q.isLoading && <div className="h-4 w-64 animate-pulse rounded bg-muted" />}
      {c && (
        <>
          <div className="text-sm">
            {c.type_label} · {c.legal_names.join(", ") || "—"}
          </div>
          <div className="text-xs text-muted-foreground">
            статьи финотчёта: {c.accrual_items.join(", ") || "начислений нет"}
            {c.stats_table ? ` · статистика: ${c.stats_table}` : " · статистики Ozon нет"}
          </div>
        </>
      )}
      {q.isError && <ErrorBox error={q.error} what="кампанию" />}
    </div>
  );
}

function CampaignBlocks({
  campaignId,
  filters,
  warning,
  campaign,
  onOpenCard,
}: {
  campaignId: string;
  filters: OzFilters;
  warning: string | null;
  campaign: CampaignQuery;
  onOpenCard: (card: CardRef) => void;
}): React.ReactElement {
  const scope = useMemo(() => [`obj:campaign:${campaignId}`], [campaignId]);
  // карточка кампании — только у Ozon (mp.ts)
  const summary = useSummary("ozon", filters, scope);
  const c = campaign.data;
  return (
    <div className="space-y-5">
      <Block title="Показатели периода">
        {(summary.isLoading || campaign.isLoading) && <Skeleton rows={2} />}
        {summary.isError && <ErrorBox error={summary.error} what="начисления кампании" />}
        {summary.data && c && (
          <>
            <KpiGrid kpis={campaignKpis(summary.data, c)} warning={warning} compact />
            <AccrualNote summary={summary.data} campaign={c} />
          </>
        )}
        {/* статистика не загрузилась (не 404): «Начислено» из сводки + текст ошибки, не пустота */}
        {summary.data && !c && campaign.isError && (
          <>
            <KpiGrid kpis={[accruedKpi(summary.data)]} warning={warning} compact />
            <ErrorBox error={campaign.error} what="статистику кампании" />
          </>
        )}
      </Block>
      {c && c.type !== null && <DaysBlock campaign={c} />}
      {c && c.type !== null && c.type !== "no_report" && (
        <SkusBlock campaign={c} onOpenCard={onOpenCard} />
      )}
    </div>
  );
}

/** «Начислено» (финотчёт) против «расхода по статистике» (кабинет): разница больше 1% от
 *  |статистики| (и не меньше 0,5 ₽) — обе суммы и разница плашкой; иначе — короткая строка. */
function AccrualNote({
  summary,
  campaign,
}: {
  summary: OzSummary;
  campaign: OzCampaign;
}): React.ReactElement {
  const chk = accrualCheck(summary, campaign);
  const missing = campaign.totals.cur.stats?.spend_missing ?? 0;
  const corr =
    Math.abs(chk.corrections) >= 0.5 ? ` (в т.ч. корректировки Ozon ${rub(chk.corrections)})` : "";
  return (
    <div className="space-y-1 text-xs" data-testid="accrual-vs-stats">
      {chk.stats === null ? (
        <div className="text-muted-foreground">
          Статистики рекламного кабинета по кампании нет — только начисления финотчёта.
        </div>
      ) : chk.diverges ? (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
          data-testid="accrual-diverges"
        >
          <div className="font-medium">
            Начислено {rub(chk.accrued)} ≠ расход по статистике {rub(chk.stats)}
            {corr}: разница {rub(chk.diff!)}
            {chk.diffPct !== null && ` (${pct(chk.diffPct)})`}
          </div>
          <div className="mt-0.5">
            «Начислено» — {ACCRUED_HINT.charAt(0).toLowerCase() + ACCRUED_HINT.slice(1)}.
            «Расход по статистике» — {STATS_HINT.charAt(0).toLowerCase() + STATS_HINT.slice(1)}.
            Расходятся из-за НДС, бонусов, корректировок; у «оплаты за заказ» — отмены и заказы,
            начисленные в другом периоде. Деньги, которые удержал Ozon, — «начислено».
          </div>
        </div>
      ) : (
        <div className="text-muted-foreground">
          Начислено по финотчёту и расход по статистике{corr} совпадают в пределах допуска: разница{" "}
          {rub(chk.diff!)}
          {chk.diffPct !== null && ` (${pct(chk.diffPct)})`} — не больше 1% или меньше 0,5 ₽.
        </div>
      )}
      {missing > 0 && (
        <div className="text-amber-700 dark:text-amber-400">
          {int(missing)} строк статистики без расхода — в суммы не вошли.
        </div>
      )}
    </div>
  );
}

type DayPoint = OzCampaign["days"][number] & { label: string };

/** Расход и заказы по дням периода — два малых графика, у каждого своя шкала (₽ и шт.). */
function DaysBlock({ campaign }: { campaign: OzCampaign }): React.ReactElement {
  const isDark = useIsDark();
  const points: DayPoint[] = useMemo(
    () =>
      campaign.days.map((d) => ({ ...d, label: `${d.date.slice(8, 10)}.${d.date.slice(5, 7)}` })),
    [campaign.days],
  );
  const spendColor = isDark ? "#d55181" : "#e87ba4";
  const ordersColor = isDark ? "#3987e5" : "#2a78d6";
  const hasOrders = campaign.type !== "no_report";
  return (
    <Block title="По дням (статистика Ozon)">
      <div className={cn("grid gap-3", hasOrders && "sm:grid-cols-2")} data-testid="campaign-days">
        <MiniBars
          title="Расход по статистике, ₽"
          points={points}
          dataKey="spend"
          color={spendColor}
          fmt={rub}
        />
        {hasOrders && (
          <MiniBars
            title="Заказы, шт."
            points={points}
            dataKey="orders"
            color={ordersColor}
            fmt={int}
          />
        )}
      </div>
    </Block>
  );
}

function MiniBars({
  title,
  points,
  dataKey,
  color,
  fmt,
}: {
  title: string;
  points: DayPoint[];
  dataKey: "spend" | "orders";
  color: string;
  fmt: (n: number) => string;
}): React.ReactElement {
  const isDark = useIsDark();
  return (
    <div className="rounded-md border bg-card p-2">
      <div className="mb-1 text-xs text-muted-foreground">{title}</div>
      <ResponsiveContainer width="100%" height={130}>
        <BarChart data={points} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} strokeOpacity={0.2} />
          <XAxis dataKey="label" tick={axisTick(isDark)} tickLine={false} axisLine={false} />
          <YAxis
            tick={axisTick(isDark)}
            tickLine={false}
            axisLine={false}
            width={52}
            tickFormatter={(v: number) => compact(v)}
          />
          <Tooltip
            cursor={{ fillOpacity: 0.06 }}
            content={<DayTip dataKey={dataKey} title={title} fmt={fmt} />}
          />
          <Bar
            dataKey={dataKey}
            fill={color}
            radius={[3, 3, 0, 0]}
            maxBarSize={24}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function DayTip({
  active,
  payload,
  dataKey,
  title,
  fmt,
}: {
  active?: boolean;
  payload?: { payload: DayPoint }[];
  dataKey: "spend" | "orders";
  title: string;
  fmt: (n: number) => string;
}): React.ReactElement | null {
  const p = active ? payload?.[0]?.payload : undefined;
  if (!p) return null;
  const value = p[dataKey];
  return (
    <div className="rounded-md border bg-background px-3 py-2 text-xs text-foreground shadow-md">
      <div className="mb-1 font-medium">{p.date.split("-").reverse().join(".")}</div>
      <div className="flex justify-between gap-4 tabular-nums">
        <span>{title}</span>
        <span>{value === null ? "—" : fmt(value)}</span>
      </div>
      {p.clicks !== null && (
        <div className="flex justify-between gap-4 tabular-nums text-muted-foreground">
          <span>Клики</span>
          <span>{int(p.clicks)}</span>
        </div>
      )}
    </div>
  );
}

/** Колонки таблицы артикулов по виду кампании (у «оплаты за заказ» нет показов и кликов). */
function skuColumns(type: OzCampaign["type"]): {
  head: string;
  cell: (s: CampaignSku) => string;
}[] {
  const n = (v: number | null): string => (v === null ? "—" : int(v));
  const tail = [
    { head: "Расход", cell: (s: CampaignSku) => rub(s.spend) },
    { head: "ДРР", cell: (s: CampaignSku) => pct(s.drr) },
  ];
  if (type === "order") {
    return [
      { head: "Заказы", cell: (s) => n(s.orders) },
      { head: "Шт.", cell: (s) => n(s.qty) },
      { head: "Сумма заказов", cell: (s) => (s.revenue === null ? "—" : rub(s.revenue)) },
      ...tail,
    ];
  }
  return [
    { head: "Показы", cell: (s) => n(s.views) },
    { head: "Клики", cell: (s) => n(s.clicks) },
    { head: "Заказы", cell: (s) => n(s.orders) },
    { head: "Выручка", cell: (s) => (s.revenue === null ? "—" : rub(s.revenue)) },
    ...tail,
  ];
}

/** SKU кампании за период, дороже первыми (топ-50 с бэкенда); артикул — из справочника витрины,
 *  у артикула — значок его карточки. */
function SkusBlock({
  campaign,
  onOpenCard,
}: {
  campaign: OzCampaign;
  onOpenCard: (card: CardRef) => void;
}): React.ReactElement {
  const cols = skuColumns(campaign.type);
  const rest = campaign.sku_count - campaign.skus.length;
  return (
    <Block title="Артикулы кампании">
      {campaign.skus.length === 0 ? (
        <div className="text-xs text-muted-foreground">Нет статистики по SKU в периоде.</div>
      ) : (
        <div className="overflow-x-auto rounded-md border bg-card">
          <table className="w-full text-xs" data-testid="campaign-skus-table">
            <thead className="bg-muted/40">
              <tr>
                <th className={cn(thClass, "text-left")}>Артикул</th>
                {cols.map((c) => (
                  <th key={c.head} className={thClass}>
                    {c.head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {campaign.skus.map((s) => (
                <tr key={`${s.legal_name}|${s.sku}`} className="border-t">
                  <td className="max-w-[16rem] px-2 py-1">
                    <div className="flex items-start gap-1">
                      <span className="font-medium">{s.offer_id ?? `SKU ${s.sku ?? "—"}`}</span>
                      {s.offer_id !== null && (
                        <CardButton
                          label={`Карточка артикула ${s.offer_id}`}
                          title="Карточка артикула"
                          onOpen={() => onOpenCard({ kind: "article", key: s.offer_id! })}
                        />
                      )}
                    </div>
                    <div
                      className="truncate text-muted-foreground"
                      title={s.name ?? undefined}
                    >
                      {s.offer_id === null
                        ? "нет в справочнике mp_finance_sku_dim"
                        : `SKU ${s.sku}${s.name ? ` · ${s.name}` : ""}`}
                    </div>
                  </td>
                  {cols.map((c) => (
                    <td key={c.head} className={tdNum}>
                      {c.cell(s)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {rest > 0 && (
            <div className="px-2 py-1 text-xs text-muted-foreground">и ещё {rest} SKU</div>
          )}
        </div>
      )}
    </Block>
  );
}
